import type { FunctionComponent } from 'react';

import { MarkdownNode } from '../../../components/markdown/MarkdownNode';
import { type MarkdownParsedNode, MarkdownParser } from '../../../components/markdown/MarkdownParser';
import type { RenderableDynamicValues } from '../types';

import { DynamicValueNode } from './DynamicValueNode';

export interface DictionaryValueProps {
  value: string;
  markdown?: boolean;
  renderableDynamicValues?: RenderableDynamicValues;
  dynamicValues?: Record<string, string | number>;
}

// Utils
const markdownBoldRegex = /\*\*(.*?)\*\*/g;
const markdownUnderlinedReg = /__(.*?)__/g;

const parseMarkdown = (text: string, dynamicValues?: Record<string, string | number>): string => {
  let formattedText = text
    .replace(markdownBoldRegex, '<b>$1</b>') // Replace ** with <b></b> for bold text
    .replace(markdownUnderlinedReg, '<u>$1</u>'); // Replace __ with <u></u> for underlined text

  // Substitute dynamic values
  if (dynamicValues) {
    Object.keys(dynamicValues).forEach((key) => {
      const value = dynamicValues[key];
      if (value) {
        const regex = new RegExp(`@@${key}@@`, 'g');
        formattedText = formattedText.replace(regex, value.toString());
      }
    });
  }

  return formattedText;
};

export const DictionaryValue: FunctionComponent<DictionaryValueProps> = ({
  value,
  markdown,
  renderableDynamicValues,
  dynamicValues,
}) => {
  if (!value) return '\u00A0';

  const parsedValue = value;

  if (renderableDynamicValues) {
    let markdownNodes: MarkdownParsedNode[] = [];
    if (markdown) {
      markdownNodes = MarkdownParser.parse(value);
    } else {
      markdownNodes = [{ type: 'text', value }];
    }
    return markdownNodes.map((node, index) => {
      return (
        <MarkdownNode type={node.type} key={index}>
          <DynamicValueNode dynamicValues={renderableDynamicValues}>{node.value}</DynamicValueNode>
        </MarkdownNode>
      );
    });
  }

  if (markdown) {
    const formattedContent = parseMarkdown(value, dynamicValues);
    return <span dangerouslySetInnerHTML={{ __html: formattedContent }} />;
  }

  return parsedValue;
};
